import React from 'react';
import { CheckCircle, Clock } from 'lucide-react';
import Button from '../../../../components/Button';

interface BrandBriefCardProps {
  brief: Record<string, any>;
  status: string;
  briefApprovedAt?: string | null;
  onApprove: () => void;
  loading: boolean;
}

const BrandBriefCard: React.FC<BrandBriefCardProps> = ({ brief, status, briefApprovedAt, onApprove, loading }) => {
  if (!brief || Object.keys(brief).length === 0) return null;

  const isApproved = !!briefApprovedAt;
  const canApprove = status === 'planned' && !isApproved;
  const keywords: string[] = Array.isArray(brief.styleKeywords) ? brief.styleKeywords : [];
  const surfaces: string[] = Array.isArray(brief.targetSurfaces) ? brief.targetSurfaces : [];

  const items = [
    { label: 'Business Type', value: brief.businessType },
    { label: 'Objectives', value: brief.objectives },
    { label: 'Summary', value: brief.summary },
  ].filter((i) => i.value);

  return (
    <div className="rounded-xl border border-primary-100 bg-white p-5 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-[15px] font-bold text-text-dark">Brand Brief</h3>
        <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[10px] font-semibold ${
          isApproved ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
        }`}>
          {isApproved ? <><CheckCircle className="h-3 w-3" /> Approved</> : <><Clock className="h-3 w-3" /> Pending</>}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.label} className="rounded-lg bg-primary-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-purple mb-1">{item.label}</div>
            <div className="text-xs text-text-dark leading-relaxed">{String(item.value)}</div>
          </div>
        ))}
        {keywords.length > 0 && (
          <div className="rounded-lg bg-primary-50 p-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-purple mb-1">Style Keywords</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {keywords.map((kw) => (
                <span key={kw} className="rounded-full bg-primary-200 px-2 py-0.5 text-[10px] font-medium text-brand-purple">{kw}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {surfaces.length > 0 && (
        <div className="mt-3 rounded-lg bg-primary-50 p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-purple mb-1">Target Surfaces</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {surfaces.map((s) => (
              <span key={s} className="rounded-full bg-primary-200 px-2 py-0.5 text-[10px] font-medium text-brand-purple">{s}</span>
            ))}
          </div>
        </div>
      )}

      {canApprove && (
        <div className="mt-4">
          <Button variant="primary" size="small" onClick={onApprove} disabled={loading}>
            <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
            Approve Brief
          </Button>
        </div>
      )}
    </div>
  );
};

export default BrandBriefCard;
